import { Blockquote, FormLabel, Loading, TextInput } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import ExcelUploader from "components/Shared/ExcelUploader/ExcelUploader"
import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"
import useCommon from "hooks/useCommon"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Select from "react-select"
import {
  useGetAllBaseXlsFormQuery,
  useSaveXlsFormMutation,
  useUploadXlsFormFileMutation,
} from "services/rpme/xlsFormSchemas"
import { XlsFormResult } from "interfaces/xlsForm"
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { XlsFormStatus } from "constants/constants"
import { FieldsData } from "interfaces/mappingInterfaces"
import XlsFormFieldMapping from "components/XlsForm/XlsFormFieldMapping"
import ValidationFormSchema from "./ValidationFormSchema"

export default function RPMEAddXlsFormSchema() {
  const navigate = useNavigate()
  const { showSnackBar } = useCommon()

  const [saveXls, { data: fileHeaders, reset }] = useUploadXlsFormFileMutation()
  const [saveXlsForm, { isLoading }] = useSaveXlsFormMutation()
  const { data: baseSchemas, isFetching: loadingSchemas } = useGetAllBaseXlsFormQuery()

  const [name, setName] = useState("")
  const [file, setFile] = useState<File>()
  const [fieldsData, setFieldsData] = useState<FieldsData>({
    questionLabelPosition: 0,
    choiceLabelPosition: 0,
  })
  const [baseSchemaId, setBaseSchemaId] = useState<number | undefined>(undefined)
  const [adm0code, setAdm0code] = useState<number | undefined>(undefined)
  const [errorSaveXls, setErrorSaveXls] = useState<XlsFormResult | null>(null)

  const handleCreate = async () => {
    if (
      !adm0code ||
      !baseSchemaId ||
      !name ||
      adm0code === -1 ||
      !file ||
      !(fieldsData.questionLabelPosition && fieldsData.choiceLabelPosition)
    ) {
      showSnackBar("Please fill all required fields", "warning")
      return
    }
    try {
      await saveXlsForm({
        baseXlsFormSchemaId: baseSchemaId,
        adm0code,
        name,
        file,
        questionLabelPosition: fieldsData.questionLabelPosition,
        choiceLabelPosition: fieldsData.choiceLabelPosition,
      }).unwrap()

      showSnackBar("XLS Form schema added successfully!")
      navigate(window.location.pathname.replace("/add", ""))
    } catch (err) {
      const error = err as FetchBaseQueryError

      if (error.status === XlsFormStatus.ValidationError) {
        setErrorSaveXls(error.data as XlsFormResult)
      } else showSnackBar("An error occurred while processing your request", "error")
    }
  }

  const handleClear = () => {
    setFile(undefined)
    reset()
    setFieldsData({ questionLabelPosition: 0, choiceLabelPosition: 0 })
    setErrorSaveXls(null)
  }

  const handleFileUpload = async (fileUpload: File): Promise<void> => {
    try {
      const formData = new FormData()
      formData.append("file", fileUpload)
      await saveXls(formData).unwrap()
    } catch (error) {
      handleClear()
      showSnackBar("An error occurred while processing your request", "error")
    }
  }

  useEffect(() => {
    if (file) handleFileUpload(file)
  }, [file])

  return (
    <div>
      <div className="space-y-5 w-6/12">
        <div>
          <CountrySelector
            isClearable={false}
            allowAll={false}
            label="Country"
            onSelect={country => setAdm0code(country?.value)}
            className="w-full"
          />
        </div>

        <TextInput
          labelText="Name"
          name="name"
          placeholder="Name of the xls form"
          type="text"
          onChange={e => setName(e.target.value)}
          className="border-gray-300 rounded-md text-base"
        />

        <div>
          <FormLabel htmlFor="base-xls">Base schema</FormLabel>
          <Select
            inputId="base-xls"
            isLoading={loadingSchemas}
            isSearchable
            placeholder="Select base schema"
            getOptionLabel={option => option.xlsFormName}
            getOptionValue={option => option.xlsFormId.toString()}
            value={baseSchemas?.find(item => item.xlsFormId === baseSchemaId) ?? null}
            options={baseSchemas ?? []}
            onChange={item => setBaseSchemaId(item?.xlsFormId)}
          />
        </div>

        <ExcelUploader clearAll={!file} onSelect={setFile} />
        {fileHeaders && (
          <XlsFormFieldMapping
            surveyList={fileHeaders.surveyHeaders}
            choiceList={fileHeaders.choicesHeaders}
            values={fieldsData}
            setValues={setFieldsData}
          />
        )}

        {errorSaveXls?.resultMessage && (
          <Blockquote kind="error" withIcon style={{ padding: "2rem" }}>
            {errorSaveXls.resultMessage}
          </Blockquote>
        )}

        {fileHeaders && errorSaveXls && <ValidationFormSchema data={errorSaveXls} />}

        <Loading active={isLoading} />

        {fileHeaders && (
          <div className="d-flex">
            {!errorSaveXls && <Button action={handleCreate} label="Save" className="btn-full blue mr-1" />}
            <Button action={handleClear} label="Clear & Re-Upload" className="btn-full blue " />
          </div>
        )}
      </div>
    </div>
  )
}
